<template>
  <div class="settings-page">
    <header class="page-header">
      <div>
        <h1>Настройки коммерческого калькулятора</h1>
        <p>Активная версия: <strong>v{{ activeVersion || '—' }}</strong> · алгоритм {{ activeConfig.algorithm_version || '—' }}</p>
      </div>
      <div class="header-actions">
        <button class="btn" type="button" :disabled="busy" @click="loadPage">Обновить</button>
        <button v-if="canWrite" class="btn primary" type="button" :disabled="busy || draft.id" @click="createDraft(activeVersion)">{{ savedDraft.id ? 'Продолжить черновик' : 'Создать черновик' }}</button>
      </div>
    </header>

    <div v-if="error" class="message error">{{ error }}</div>
    <div v-if="notice" class="message success">{{ notice }}</div>
    <my-loader v-if="loading && !loaded" />

    <template v-else>
      <section v-if="draft.id" class="draft-bar">
        <div>
          <strong>Черновик {{ draft.id }}<template v-if="draft.version"> · на основе v{{ draft.version }}</template></strong>
          <span>{{ validationStatus(draft) }} · изменён {{ draft.updated_at ? formatDate(draft.updated_at) : 'не сохранён' }}<template v-if="draft.validated_at"> · проверен {{ formatDate(draft.validated_at) }}</template></span>
        </div>
        <div class="header-actions">
          <button v-if="canWrite" class="btn" type="button" :disabled="busy" @click="validateDraft">Проверить</button>
          <button v-if="canWrite" class="btn" type="button" :disabled="busy" @click="previewDraft()">Предпросмотр</button>
          <button v-if="canWrite" class="btn primary" type="button" :disabled="busy" @click="saveDraft">Сохранить</button>
          <button v-if="canWrite" class="btn danger" type="button" :disabled="busy" @click="deleteDraft(draft)">Удалить</button>
          <button v-if="canPublish" class="btn publish" type="button" :disabled="busy" @click="publishDraft">Опубликовать</button>
        </div>
      </section>

      <div v-if="validationMessages.length" class="message validation">
        <strong>Результат проверки</strong>
        <div v-for="(message, index) in validationMessages" :key="index">{{ message }}</div>
      </div>

      <section v-if="savedDraft.id && !draft.id" class="saved-draft-panel">
        <div>
          <h2>Сохранённый черновик</h2>
          <p>Изменён {{ formatDate(savedDraft.updated_at) }} · на основе v{{ savedDraft.version || '—' }} · {{ validationStatus(savedDraft) }}<template v-if="savedDraft.validated_at"> · проверен {{ formatDate(savedDraft.validated_at) }}</template></p>
        </div>
        <div class="header-actions">
          <button class="btn primary" type="button" :disabled="busy" @click="continueDraft">Продолжить</button>
          <button class="btn" type="button" :disabled="busy" @click="previewDraft(savedDraft)">Предпросмотр</button>
          <button class="btn danger" type="button" :disabled="busy" @click="deleteDraft(savedDraft)">Удалить</button>
        </div>
      </section>

      <section class="card history-section">
        <div class="history-heading">
          <div>
            <h2>История опубликованных версий</h2>
            <p>Опубликованные версии неизменяемы. Их можно просматривать, брать за основу черновика или восстанавливать.</p>
          </div>
        </div>
        <div v-if="!sortedHistory.length" class="empty">Опубликованных версий пока нет.</div>
        <div v-else class="history-strip">
          <article
            v-for="item in sortedHistory"
             :key="versionOf(item)"
             class="history-item"
             :class="{
               active: String(versionOf(item)) === String(activeVersion),
               viewed: String(versionOf(item)) === String(viewedVersion),
             }"
          >
            <div class="history-version">
               <strong>v{{ versionOf(item) }}</strong>
               <span v-if="String(versionOf(item)) === String(activeVersion)" class="active-badge">Активна</span>
               <span v-else-if="String(versionOf(item)) === String(viewedVersion)" class="viewed-badge">Просматривается</span>
            </div>
            <span class="history-date">{{ item.published_at ? 'Опубликована' : 'Создана' }}: {{ formatDate(item.published_at || item.created_at || item.updated_at) }}</span>
            <p>{{ item.reason || item.change_reason || 'Причина публикации не указана' }}</p>
            <div class="history-actions">
               <button class="link" type="button" :disabled="busy || draft.id" @click="viewVersion(versionOf(item))">Просмотр</button>
              <button v-if="String(versionOf(item)) !== String(activeVersion)" class="link" type="button" @click="previewVersion(versionOf(item))">Предпросмотр</button>
              <button v-if="canWrite" class="link" type="button" :disabled="busy || draft.id" @click="createDraft(versionOf(item))">Создать черновик</button>
              <button v-if="canPublish && String(versionOf(item)) !== String(activeVersion)" class="link danger" type="button" :disabled="busy" @click="rollbackVersion(versionOf(item))">Откатить</button>
            </div>
          </article>
         </div>
       </section>

       <div v-if="!draft.id && viewedVersion && String(viewedVersion) !== String(activeVersion)" class="archive-view-bar">
         <span>Показана архивная версия <strong>v{{ viewedVersion }}</strong>. Настройки доступны только для просмотра.</span>
         <button class="link" type="button" :disabled="busy" @click="viewVersion(activeVersion)">Вернуться к активной версии</button>
       </div>

       <main class="editor">
          <section class="card">
            <h2>Параметры сделки</h2>
            <div class="fields-grid">
              <label v-for="field in dealFields" :key="field.key">
                <span>{{ field.label }}</span>
                <input v-model="editableConfig.deal[field.key]" :disabled="!canWrite || !draft.id" type="text" inputmode="decimal" />
              </label>
              <label>
                <span>Ставки зарплаты менеджера</span>
                <input v-model="managerSalaryRates" :disabled="!canWrite || !draft.id" type="text" placeholder="0.17, 0.20" />
              </label>
              <label>
                <span>Ставки НДС перевозчика</span>
                <input v-model="carrierVatRates" :disabled="!canWrite || !draft.id" type="text" placeholder="0.05, 0.07, 0.22" />
              </label>
            </div>
          </section>

          <section class="card">
            <div class="section-title">
              <div><h2>Стратегии</h2><p>Маржинальность сценариев расчета.</p></div>
            </div>
            <div class="table-wrap">
              <table>
                <thead><tr><th>Ключ</th><th>Название</th><th>Маржа</th></tr></thead>
                <tbody>
                  <tr v-for="(item, index) in editableConfig.strategies" :key="`${item.key}-${index}`">
                    <td><span class="read-only-value">{{ item.key || '—' }}</span></td>
                    <td><span class="read-only-value">{{ item.name || '—' }}</span></td>
                    <td><input v-model="item.margin" :disabled="!canWrite || !draft.id" inputmode="decimal" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="card">
            <h2>Субсидирование</h2>
            <div class="fields-grid">
              <label v-for="field in subsidyFields" :key="field.key">
                <span>{{ field.label }}</span>
                <input v-model="editableConfig.subsidy[field.key]" :disabled="!canWrite || !draft.id" :type="field.integer ? 'number' : 'text'" />
              </label>
            </div>
            <div class="section-title compact">
              <h3>Кривая скидки</h3>
              <button v-if="canWrite && draft.id" class="btn small" type="button" @click="addCurvePoint">Добавить точку</button>
            </div>
            <div class="table-wrap narrow">
              <table>
                <thead><tr><th>Дни</th><th>Скидка</th><th v-if="canWrite && draft.id"></th></tr></thead>
                <tbody>
                  <tr v-for="(item, index) in editableConfig.subsidy.curve" :key="index">
                    <td><input v-model.number="item.days" :disabled="!canWrite || !draft.id" type="number" /></td>
                    <td><input v-model="item.discount" :disabled="!canWrite || !draft.id" inputmode="decimal" /></td>
                    <td v-if="canWrite && draft.id"><button class="remove" type="button" @click="removeCurvePoint(index)">×</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="card">
            <div class="section-title"><h2>Клиенты</h2><button v-if="canWrite && draft.id" class="btn small" type="button" @click="addClient">Добавить</button></div>
            <div class="table-wrap">
              <table>
                <thead><tr><th class="drag-column"><span class="visually-hidden">Порядок</span></th><th>Ключ</th><th>Название</th><th>Базовый срок</th><th>Рабочих дней</th><th v-if="canWrite && draft.id"></th></tr></thead>
                <draggable
                  v-model="editableConfig.clients"
                  tag="tbody"
                  :item-key="clientItemKey"
                  :disabled="!canReorder"
                  handle=".drag-handle"
                  ghost-class="reorder-ghost"
                  @end="onClientsReordered"
                >
                  <template #item="{ element: item, index }">
                  <tr>
                    <td class="drag-column">
                      <button
                        class="drag-handle"
                        type="button"
                        :disabled="!canReorder"
                        :aria-label="`Изменить порядок клиента ${item.name || item.key || index + 1}`"
                        :title="canReorder ? 'Перетащите, чтобы изменить порядок' : 'Изменение порядка недоступно'"
                      ><span aria-hidden="true">⣿</span></button>
                    </td>
                    <td><input v-model="item.key" :disabled="!canWrite || !draft.id" /></td><td><input v-model="item.name" :disabled="!canWrite || !draft.id" /></td>
                    <td><input v-model="item.base_term_label" :disabled="!canWrite || !draft.id" /></td><td><input v-model.number="item.base_business_days" :disabled="!canWrite || !draft.id" type="number" /></td>
                    <td v-if="canWrite && draft.id"><button class="remove" type="button" @click="removeRow('clients', index)">×</button></td>
                  </tr>
                  </template>
                </draggable>
              </table>
            </div>
          </section>

          <section class="card">
            <div class="section-title"><h2>Условия оплаты</h2><button v-if="canWrite && draft.id" class="btn small" type="button" @click="addPaymentTerm">Добавить</button></div>
            <div class="table-wrap">
              <table>
                <thead><tr><th class="drag-column"><span class="visually-hidden">Порядок</span></th><th>Ключ</th><th>Срок</th><th>Рабочих дней</th><th>Аванс</th><th>Согласование</th><th v-if="canWrite && draft.id"></th></tr></thead>
                <draggable
                  v-model="editableConfig.payment_terms"
                  tag="tbody"
                  :item-key="paymentTermItemKey"
                  :disabled="!canReorder"
                  handle=".drag-handle"
                  ghost-class="reorder-ghost"
                  @end="onPaymentTermsReordered"
                >
                  <template #item="{ element: item, index }">
                    <tr>
                    <td class="drag-column">
                      <button
                        class="drag-handle"
                        type="button"
                        :disabled="!canReorder"
                        :aria-label="`Изменить порядок условия оплаты ${item.term_label || item.key || index + 1}`"
                        :title="canReorder ? 'Перетащите, чтобы изменить порядок' : 'Изменение порядка недоступно'"
                      ><span aria-hidden="true">⣿</span></button>
                    </td>
                    <td><input v-model="item.key" :disabled="!canWrite || !draft.id" /></td><td><input v-model="item.term_label" :disabled="!canWrite || !draft.id" /></td>
                    <td><input v-model.number="item.business_days" :disabled="!canWrite || !draft.id" type="number" /></td><td><input v-model="item.advance_share" :disabled="!canWrite || !draft.id" /></td>
                    <td><input v-model="item.approval" :disabled="!canWrite || !draft.id" /></td><td v-if="canWrite && draft.id"><button class="remove" type="button" @click="removeRow('payment_terms', index)">×</button></td>
                    </tr>
                  </template>
                </draggable>
              </table>
            </div>
          </section>

          <section class="card">
            <h2>Расчетная матрица скидок</h2>
            <p class="muted">Формируется сервером из целевой маржи стратегий и ставок НДС.</p>
            <div v-if="derivedRows.length" class="table-wrap">
              <table><thead><tr><th v-for="column in derivedColumns" :key="column">{{ formatDerivedColumnLabel(column) }}</th></tr></thead>
                <tbody><tr v-for="(row, index) in derivedRows" :key="index"><td v-for="column in derivedColumns" :key="column">{{ column === 'key' ? formatDerivedStrategyLabel(row[column], editableConfig.strategies) : displayCell(row[column]) }}</td></tr></tbody>
              </table>
            </div>
            <div v-else class="empty">Матрица пока не рассчитана. Сохраните черновик и запустите предпросмотр.</div>
          </section>
      </main>
    </template>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { createToaster } from '@meforma/vue-toaster'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import draggable from 'vuedraggable'
import {
  createCommercialCalculatorConfigDraft,
  deleteCommercialCalculatorConfigDraft,
  getCommercialCalculatorConfig,
  getCommercialCalculatorConfigDraft,
  getCommercialCalculatorConfigDrafts,
  getCommercialCalculatorConfigHistory,
  getCommercialCalculatorConfigVersion,
  publishCommercialCalculatorConfigDraft,
  rollbackCommercialCalculatorConfigVersion,
  updateCommercialCalculatorConfigDraft,
  validateCommercialCalculatorConfigDraft,
} from '@/backend/commercialCalculatorApi'
import {
  formatApiErrorDetail,
  formatDerivedColumnLabel,
  formatDerivedStrategyLabel,
  formatValidationReport,
  isDraftValidated,
  normalizeConfigEnvelope,
  normalizeDrafts,
  normalizeDerivedMatrix,
  normalizeEditableConfig,
  normalizeHistory,
  normalizePreviewDraftTarget,
  normalizeVisibleOrder,
  sortHistoryLatestFirst,
  toConfigPayload,
  unwrapApiObject,
} from '@/pages/commercialCalculator/configMapping.mjs'

const DEAL_FIELDS = [
  ['customer_vat_rate', 'НДС заказчика'], ['profit_reserve_rate', 'Резерв прибыли'], ['tax_fund_rate', 'Налоговый фонд'],
  ['min_deal_result', 'Минимальный результат'], ['kpi_other_client_rate', 'KPI обычного клиента'],
  ['kpi_inbev_high_rate', 'KPI INBEV/EFES'], ['kpi_high_profitability_threshold', 'Порог высокой прибыльности'],
].map(([key, label]) => ({ key, label }))

const SUBSIDY_FIELDS = [
  ['target_annual_yield', 'Целевая годовая доходность'], ['business_to_calendar_days_factor', 'Коэффициент рабочих/календарных дней'],
  ['documents_extra_calendar_days', 'Доп. дней на документы', true], ['max_discount', 'Максимальная скидка'],
  ['discount_round_step', 'Шаг округления'], ['prepayment_reference_discount', 'Эталонная скидка предоплаты'],
  ['prepayment_reference_base_days', 'Базовые дни предоплаты', true], ['prepayment_reference_term_days', 'Срок эталонной предоплаты', true],
  ['prepayment_reference_advance_share', 'Доля эталонного аванса'],
].map(([key, label, integer = false]) => ({ key, label, integer }))

function errorText(error) {
  const detail = error?.response?.data?.detail
  return formatApiErrorDetail(detail || error?.response?.data?.message || error?.message)
}

export default {
  name: 'CommercialCalculatorSettingsPage',
  components: { draggable },
  setup() {
    const store = useStore()
    const router = useRouter()
    const toaster = createToaster()
    const loading = ref(false)
    const loaded = ref(false)
    const busy = ref(false)
    const error = ref('')
    const notice = ref('')
    const activeVersion = ref(null)
     const activeConfig = ref(normalizeEditableConfig({}))
     const editableConfig = ref(normalizeEditableConfig({}))
     const viewedVersion = ref(null)
    const history = ref([])
    const derived = ref([])
    const validationMessages = ref([])
    const emptyDraft = () => ({ id: null, status: null, updated_at: null, version: null, checksum: null, validated_checksum: null, validated_at: null, validation_report: null })
    const draft = ref(emptyDraft())
    const savedDraft = ref(emptyDraft())
    const canWrite = computed(() => store.getters.hasPermission('calculator.settings.write'))
    const canPublish = computed(() => store.getters.hasPermission('calculator.settings.publish'))
    const canReorder = computed(() => canWrite.value && Boolean(draft.value.id))
    const itemKeys = new WeakMap()
    let nextItemKey = 0
    const stableItemKey = (prefix) => (item) => {
      if (!itemKeys.has(item)) itemKeys.set(item, `${prefix}-${nextItemKey += 1}`)
      return itemKeys.get(item)
    }
    const clientItemKey = stableItemKey('client')
    const paymentTermItemKey = stableItemKey('payment-term')

    const managerSalaryRates = computed({
      get: () => editableConfig.value.deal.manager_salary_rates.join(', '),
      set: (value) => { editableConfig.value.deal.manager_salary_rates = value.split(',').map((item) => item.trim()).filter(Boolean) },
    })
    const carrierVatRates = computed({
      get: () => editableConfig.value.deal.carrier_vat_rates.join(', '),
      set: (value) => { editableConfig.value.deal.carrier_vat_rates = value.split(',').map((item) => item.trim()).filter(Boolean) },
    })
    const derivedRows = computed(() => normalizeDerivedMatrix(derived.value))
    const derivedColumns = computed(() => [...new Set(derivedRows.value.flatMap((row) => Object.keys(row)))])
    const sortedHistory = computed(() => sortHistoryLatestFirst(history.value))

    const applyEnvelope = (response, fallbackConfig) => {
      const envelope = normalizeConfigEnvelope(response)
      const root = unwrapApiObject(response)
      const nested = root.draft && typeof root.draft === 'object' ? { ...root, ...root.draft } : root
      draft.value = {
        id: nested.id ?? nested.draft_id ?? draft.value.id,
        status: nested.status ?? nested.draft_status ?? draft.value.status,
        updated_at: nested.updated_at ?? nested.draft_updated_at ?? draft.value.updated_at,
        version: nested.version ?? nested.config_version ?? nested.based_on_version ?? draft.value.version,
        checksum: nested.checksum ?? draft.value.checksum,
        validated_checksum: Object.prototype.hasOwnProperty.call(nested, 'validated_checksum') ? nested.validated_checksum : draft.value.validated_checksum,
        validated_at: Object.prototype.hasOwnProperty.call(nested, 'validated_at') ? nested.validated_at : draft.value.validated_at,
        validation_report: Object.prototype.hasOwnProperty.call(nested, 'validation_report') ? nested.validation_report : draft.value.validation_report,
      }
       editableConfig.value = nested.config ? normalizeEditableConfig(nested.config) : normalizeEditableConfig(fallbackConfig || envelope.config)
       viewedVersion.value = null
      if (envelope.derived_adjusted_discounts && Object.keys(envelope.derived_adjusted_discounts).length) derived.value = envelope.derived_adjusted_discounts
    }

    const loadPage = async () => {
      loading.value = true
      error.value = ''
      try {
        const [configResponse, historyResponse, draftsResponse] = await Promise.all([
          getCommercialCalculatorConfig(), getCommercialCalculatorConfigHistory(),
          canWrite.value ? getCommercialCalculatorConfigDrafts() : Promise.resolve({ items: [] }),
        ])
        const envelope = normalizeConfigEnvelope(configResponse)
        activeVersion.value = envelope.active_version
       activeConfig.value = normalizeEditableConfig(envelope.config)
       if (!draft.value.id) {
         editableConfig.value = normalizeEditableConfig(envelope.config)
         viewedVersion.value = envelope.active_version
       }
        derived.value = envelope.derived_adjusted_discounts
        history.value = normalizeHistory(historyResponse)
        const drafts = normalizeDrafts(draftsResponse)
        const saved = drafts[0]
        savedDraft.value = saved ? {
          ...emptyDraft(),
          ...saved,
          version: saved.based_on_version ?? saved.version ?? saved.config_version ?? null,
        } : emptyDraft()
        loaded.value = true
      } catch (err) {
        error.value = errorText(err)
      } finally {
        loading.value = false
      }
    }

    const run = async (operation, successMessage) => {
      busy.value = true
      error.value = ''
      notice.value = ''
      try {
        const response = await operation()
        if (successMessage) {
          notice.value = successMessage
          toaster.success(successMessage)
        }
        return response
      } catch (err) {
        error.value = errorText(err)
        return null
      } finally {
        busy.value = false
      }
    }

    const createDraft = async (basedOnVersion) => {
      if (!canWrite.value) return
      if (savedDraft.value.id) {
        await continueDraft()
        return
      }
      let fallback = activeConfig.value
      if (basedOnVersion && String(basedOnVersion) !== String(activeVersion.value)) {
        const versionResponse = await run(() => getCommercialCalculatorConfigVersion(basedOnVersion))
        if (!versionResponse) return
        fallback = normalizeConfigEnvelope(versionResponse).config
      }
      const response = await run(
        () => createCommercialCalculatorConfigDraft(basedOnVersion ? { based_on_version: basedOnVersion } : {}),
        'Черновик создан',
      )
      if (response) applyEnvelope(response, fallback)
    }

    const saveDraft = async () => {
      if (!canWrite.value) return
      const response = await run(() => updateCommercialCalculatorConfigDraft(draft.value.id, {
        config: toConfigPayload(editableConfig.value),
        expected_updated_at: draft.value.updated_at,
      }), 'Черновик сохранен')
      if (response) {
        applyEnvelope(response, editableConfig.value)
        draft.value.validated_checksum = null
        draft.value.validated_at = null
        draft.value.validation_report = null
        validationMessages.value = []
      }
      return response
    }

    const validateDraft = async () => {
      if (!canWrite.value) return
      const response = await run(() => validateCommercialCalculatorConfigDraft(draft.value.id))
      if (response) {
        applyEnvelope(response, editableConfig.value)
        validationMessages.value = formatValidationReport(response)
      }
    }

    const previewDraft = async (target) => {
      if (!canWrite.value) return
      const previewTarget = normalizePreviewDraftTarget(target, draft.value)
      if (!previewTarget) {
        error.value = 'Не удалось определить черновик для предпросмотра.'
        return
      }
      const previewWindow = window.open('', '_blank')
      if (!previewWindow) {
        error.value = 'Браузер заблокировал новое окно. Разрешите всплывающие окна и повторите попытку.'
        return
      }
      let navigationStarted = false
      try {
        let previewDraftId = previewTarget.id
        if (draft.value.id && String(previewDraftId) === String(draft.value.id)) {
          const response = await saveDraft()
          if (!response) return
          previewDraftId = draft.value.id
        }
        const preparedTarget = normalizePreviewDraftTarget({ id: previewDraftId }, null)
        if (!preparedTarget) throw new Error('Не удалось определить черновик для предпросмотра.')
        const href = router.resolve({ name: 'CommercialCalculatorDraftPreview', params: { draftId: preparedTarget.id } }).href
        if (!href) throw new Error('Не удалось подготовить ссылку для предпросмотра.')
        previewWindow.location.replace(href)
        navigationStarted = true
      } catch (err) {
        error.value = errorText(err)
      } finally {
        if (!navigationStarted) previewWindow.close()
      }
    }

    const previewVersion = (version) => {
      const href = router.resolve({ name: 'CommercialCalculatorVersionPreview', params: { configVersion: version } }).href
      const previewWindow = window.open(href, '_blank')
      if (!previewWindow) {
        error.value = 'Браузер заблокировал новое окно. Разрешите всплывающие окна и повторите попытку.'
        return
      }
      previewWindow.opener = null
    }

    const continueDraft = async () => {
      if (!savedDraft.value.id) return
      const response = await run(() => getCommercialCalculatorConfigDraft(savedDraft.value.id))
      if (!response) return
      applyEnvelope(response, savedDraft.value.config)
      validationMessages.value = draft.value.validation_report ? formatValidationReport(response) : []
    }

    const deleteDraft = async (target) => {
      if (!target?.id || !window.confirm('Удалить черновик без возможности восстановления? Восстановить его будет невозможно.')) return
      const deletingOpenDraft = String(target.id) === String(draft.value.id)
      const response = await run(() => deleteCommercialCalculatorConfigDraft(target.id), 'Черновик удалён')
      if (!response) return
      if (deletingOpenDraft) {
        draft.value = emptyDraft()
        editableConfig.value = normalizeEditableConfig(activeConfig.value)
        viewedVersion.value = activeVersion.value
        validationMessages.value = []
      }
      savedDraft.value = emptyDraft()
      await loadPage()
    }

    const publishDraft = async () => {
      if (!canPublish.value) return
      const reason = window.prompt('Причина публикации')
      if (reason === null) return
      const response = await run(() => publishCommercialCalculatorConfigDraft(draft.value.id, {
        reason,
        expected_active_version: activeVersion.value,
      }), 'Настройки опубликованы')
      if (!response) return
      draft.value = emptyDraft()
      await loadPage()
    }

    const viewVersion = async (version) => {
      const response = await run(() => getCommercialCalculatorConfigVersion(version))
      if (!response) return
      const envelope = normalizeConfigEnvelope(response)
       editableConfig.value = normalizeEditableConfig(envelope.config)
       derived.value = envelope.derived_adjusted_discounts
       viewedVersion.value = version
       notice.value = `Показана версия v${version}`
    }

    const rollbackVersion = async (version) => {
      if (!canPublish.value) return
      const reason = window.prompt(`Причина отката к версии v${version}`)
      if (reason === null) return
      const response = await run(() => rollbackCommercialCalculatorConfigVersion(version, {
        reason,
        expected_active_version: activeVersion.value,
      }), `Выполнен откат к версии v${version}`)
      if (response) await loadPage()
    }

    const addCurvePoint = () => editableConfig.value.subsidy.curve.push({ days: 0, discount: '0' })
    const addClient = () => editableConfig.value.clients.push({ key: '', name: '', base_term_label: '', base_business_days: 0, active: true, order: editableConfig.value.clients.length })
    const addPaymentTerm = () => editableConfig.value.payment_terms.push({ key: '', term_label: '', business_days: 0, advance_share: '0', approval: '', active: true, order: editableConfig.value.payment_terms.length })
    const normalizeSectionOrder = (section) => normalizeVisibleOrder(editableConfig.value[section])
    const onClientsReordered = () => normalizeSectionOrder('clients')
    const onPaymentTermsReordered = () => normalizeSectionOrder('payment_terms')
    const removeRow = (section, index) => {
      editableConfig.value[section].splice(index, 1)
      normalizeSectionOrder(section)
    }
    const removeCurvePoint = (index) => editableConfig.value.subsidy.curve.splice(index, 1)
    const versionOf = (item) => item.version ?? item.config_version ?? item.id
    const formatDate = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—'
    const validationStatus = (value) => isDraftValidated(value) ? 'проверен' : 'не проверен после изменений'
    const displayCell = (value) => value && typeof value === 'object' ? JSON.stringify(value) : (value ?? '—')

    onMounted(loadPage)
    return {
      activeConfig, activeVersion, addClient, addCurvePoint, addPaymentTerm, busy, canPublish, canReorder, canWrite,
      carrierVatRates, clientItemKey, continueDraft, createDraft, dealFields: DEAL_FIELDS, deleteDraft, derivedColumns, derivedRows, displayCell,
      draft, editableConfig, error, formatDate, formatDerivedColumnLabel, formatDerivedStrategyLabel, history, loaded, loading, loadPage, managerSalaryRates,
       notice, onClientsReordered, onPaymentTermsReordered, paymentTermItemKey, previewDraft, previewVersion, publishDraft, removeCurvePoint, removeRow, rollbackVersion, saveDraft,
       savedDraft, sortedHistory, subsidyFields: SUBSIDY_FIELDS, validateDraft, validationMessages, validationStatus, versionOf, viewedVersion, viewVersion,
    }
  },
}
</script>

<style scoped>
.settings-page { min-height: 100%; padding: 20px; background: #f5f7fb; color: #1f2937; }
.page-header, .draft-bar, .section-title { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page-header { margin-bottom: 18px; }
h1 { margin: 0 0 6px; font-size: 26px; } h2 { margin: 0; font-size: 18px; } h3 { margin: 0; font-size: 15px; }
.page-header p, .section-title p, .muted { margin: 0; color: #6b7280; font-size: 13px; }
.header-actions, .history-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.btn { padding: 9px 13px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; cursor: pointer; font-weight: 600; }
.btn.primary { color: #fff; background: #2563eb; border-color: #2563eb; }.btn.publish { color: #fff; background: #059669; border-color: #059669; }.btn.danger { color: #b91c1c; border-color: #fecaca; background: #fff; }
.btn.small { padding: 6px 10px; }.btn:disabled, .link:disabled { cursor: wait; opacity: .55; }
.draft-bar, .saved-draft-panel, .card { padding: 18px; border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; box-shadow: 0 5px 16px rgba(15,23,42,.04); }
.draft-bar { margin-bottom: 14px; border-color: #bfdbfe; }.draft-bar span { display: block; margin-top: 3px; color: #6b7280; font-size: 12px; }
.saved-draft-panel { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; border-color: #93c5fd; background: #eff6ff; }.saved-draft-panel p { margin: 5px 0 0; color: #475569; font-size: 13px; }
.editor { display: grid; gap: 16px; }.card { overflow: hidden; }
.fields-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
label { display: flex; flex-direction: column; gap: 5px; color: #4b5563; font-size: 12px; }
input { box-sizing: border-box; width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 7px; background: #fff; color: #111827; }
input:disabled { background: #f8fafc; color: #475569; } input[type="checkbox"] { width: auto; }
.section-title.compact { margin-top: 18px; }.table-wrap { margin-top: 14px; overflow-x: auto; }.table-wrap.narrow { max-width: 520px; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }th, td { padding: 8px; border: 1px solid #e5e7eb; text-align: left; }th { background: #f8fafc; color: #475569; }
.read-only-value { display: inline-block; min-width: 80px; color: #475569; }
.drag-column { width: 38px; padding: 4px; text-align: center; }
.drag-handle { width: 30px; height: 30px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: #64748b; cursor: grab; font-size: 18px; }
.drag-handle:hover:not(:disabled), .drag-handle:focus-visible { background: #e2e8f0; color: #334155; outline: none; }
.drag-handle:active { cursor: grabbing; }.drag-handle:disabled { color: #cbd5e1; cursor: not-allowed; }
.reorder-ghost { opacity: .45; background: #dbeafe; }.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.remove { border: 0; background: transparent; color: #dc2626; cursor: pointer; font-size: 20px; }
.history-section { margin-bottom: 16px; }.history-heading { margin-bottom: 14px; }.history-heading p { margin: 5px 0 0; color: #6b7280; font-size: 13px; }
.history-strip { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(260px, 320px); gap: 12px; overflow-x: auto; padding: 2px 2px 10px; }
.history-item { display: flex; min-height: 150px; box-sizing: border-box; flex-direction: column; padding: 14px; border: 1px solid #e5e7eb; border-radius: 10px; background: #f8fafc; }.history-item.active { border-color: #60a5fa; background: #eff6ff; box-shadow: inset 0 0 0 1px #bfdbfe; }.history-item.viewed { box-shadow: inset 0 0 0 2px #2563eb; }
.history-version { display: flex; align-items: center; justify-content: space-between; gap: 8px; }.active-badge { padding: 3px 7px; border-radius: 999px; color: #1d4ed8; background: #dbeafe; font-size: 11px; font-weight: 700; }
.viewed-badge { padding: 3px 7px; border-radius: 999px; color: #5b21b6; background: #ede9fe; font-size: 11px; font-weight: 700; }
.history-date { display: block; margin-top: 5px; color: #6b7280; font-size: 12px; }.history-item p { flex: 1; margin: 10px 0 14px; color: #475569; font-size: 12px; }
.archive-view-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; padding: 11px 14px; border: 1px solid #c4b5fd; border-radius: 9px; color: #5b21b6; background: #f5f3ff; font-size: 13px; }
.link { padding: 0; border: 0; background: none; color: #2563eb; cursor: pointer; font-size: 12px; }.link.danger { color: #dc2626; }
.message { margin-bottom: 14px; padding: 11px 13px; border-radius: 8px; font-size: 13px; }.message.error { color: #991b1b; background: #fee2e2; }.message.success { color: #166534; background: #dcfce7; }.message.validation { color: #1e3a8a; background: #dbeafe; }
.empty { padding: 16px; border: 1px dashed #cbd5e1; border-radius: 8px; color: #64748b; font-size: 13px; }
@media (max-width: 1100px) { .fields-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 700px) { .settings-page { padding: 12px; }.page-header, .draft-bar, .saved-draft-panel, .archive-view-bar { align-items: stretch; flex-direction: column; }.fields-grid { grid-template-columns: 1fr; }.history-strip { grid-auto-columns: minmax(250px, calc(100vw - 54px)); } }
</style>
